import React, { useState, useEffect, useRef } from 'react';

const NumberRunner = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [playerY, setPlayerY] = useState(50);
  const [obstacles, setObstacles] = useState([]);
  const [correctNumbers, setCorrectNumbers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [lives, setLives] = useState(3);
  const [distance, setDistance] = useState(0);
  const [speed, setSpeed] = useState(3);
  const gameLoopRef = useRef(null);

  const generateQuestion = () => {
    const types = [
      () => {
        const a = Math.floor(Math.random() * 20) + 10;
        const b = Math.floor(Math.random() * 20) + 10;
        return { q: `${a} + ${b}`, ans: a + b };
      },
      () => {
        const a = Math.floor(Math.random() * 15) + 5;
        const b = Math.floor(Math.random() * 12) + 2;
        return { q: `${a} × ${b}`, ans: a * b };
      },
      () => {
        const a = Math.floor(Math.random() * 100) + 50;
        const b = Math.floor(Math.random() * a);
        return { q: `${a} - ${b}`, ans: a - b };
      }
    ];
    
    const gen = types[Math.floor(Math.random() * types.length)];
    return gen();
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setLives(3);
    setDistance(0);
    setSpeed(3);
    setPlayerY(50);
    setObstacles([]);
    setCorrectNumbers([]);
    setCurrentQuestion(generateQuestion());
  };

  useEffect(() => {
    if (gameState === 'playing') {
      gameLoopRef.current = setInterval(() => {
        setDistance(d => d + 1);
        
        // Move obstacles and numbers
        setObstacles(prev => prev.map(o => ({ ...o, x: o.x - speed })).filter(o => o.x > -10));
        setCorrectNumbers(prev => prev.map(n => ({ ...n, x: n.x - speed })).filter(n => n.x > -10));
        
        // Spawn obstacles
        if (Math.random() < 0.02) {
          const lanes = [20, 40, 60, 80];
          const lane = lanes[Math.floor(Math.random() * lanes.length)];
          setObstacles(prev => [...prev, {
            x: 110,
            y: lane,
            id: Date.now() + Math.random()
          }]);
        }
        
        // Spawn correct numbers
        if (Math.random() < 0.015) {
          const lanes = [20, 40, 60, 80];
          const lane = lanes[Math.floor(Math.random() * lanes.length)];
          setCorrectNumbers(prev => [...prev, {
            x: 110,
            y: lane,
            value: currentQuestion?.ans,
            id: Date.now() + Math.random()
          }]);
        }
        
        // Spawn wrong numbers
        if (Math.random() < 0.015) {
          const lanes = [20, 40, 60, 80];
          const lane = lanes[Math.floor(Math.random() * lanes.length)];
          const wrongValue = currentQuestion?.ans + Math.floor(Math.random() * 20) - 10;
          if (wrongValue !== currentQuestion?.ans) {
            setCorrectNumbers(prev => [...prev, {
              x: 110,
              y: lane,
              value: wrongValue,
              isWrong: true,
              id: Date.now() + Math.random()
            }]);
          }
        }
        
        // Check collisions with obstacles
        obstacles.forEach(obs => {
          if (Math.abs(obs.x - 10) < 5 && Math.abs(obs.y - playerY) < 8) {
            setLives(l => {
              const newLives = l - 1;
              if (newLives <= 0) setGameState('gameover');
              return newLives;
            });
            setObstacles(prev => prev.filter(o => o.id !== obs.id));
          }
        });
        
        // Check collisions with numbers
        correctNumbers.forEach(num => {
          if (Math.abs(num.x - 10) < 5 && Math.abs(num.y - playerY) < 8) {
            if (num.isWrong) {
              setLives(l => {
                const newLives = l - 1;
                if (newLives <= 0) setGameState('gameover');
                return newLives;
              });
            } else {
              setScore(s => s + 100);
              setCurrentQuestion(generateQuestion());
            }
            setCorrectNumbers(prev => prev.filter(n => n.id !== num.id));
          }
        });
        
        // Level up
        if (distance % 500 === 0 && distance > 0) {
          setLevel(l => l + 1);
          setSpeed(s => Math.min(s + 0.5, 8));
        }
      }, 50);

      return () => clearInterval(gameLoopRef.current);
    }
  }, [gameState, speed, obstacles, correctNumbers, playerY, currentQuestion, distance]);

  const movePlayer = (direction) => {
    if (direction === 'up') {
      setPlayerY(y => Math.max(20, y - 20));
    } else if (direction === 'down') {
      setPlayerY(y => Math.min(80, y + 20));
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameState !== 'playing') return;
      if (e.key === 'ArrowUp') movePlayer('up');
      if (e.key === 'ArrowDown') movePlayer('down');
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-purple-900 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-yellow-300">
          🏃 Number Runner 🔢
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🏃</div>
            <h2 className="text-3xl font-bold mb-4">Run and Collect!</h2>
            <p className="text-xl mb-8 text-gray-600">
              Pick correct numbers, dodge obstacles<br/>
              Use ↑ ↓ arrow keys to move
            </p>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              START RUNNING
            </button>
          </div>
        )}

        {gameState === 'playing' && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between items-center">
              <div className="text-xl font-bold">Score: {score}</div>
              <div className="text-xl font-bold">Level: {level}</div>
              <div className="text-xl font-bold">Distance: {distance}m</div>
              <div className="text-xl font-bold text-red-600">
                Lives: {'❤️'.repeat(lives)}
              </div>
            </div>

            {currentQuestion && (
              <div className="bg-yellow-400 text-gray-800 rounded-lg p-4 mb-4 text-center">
                <div className="text-2xl font-bold">Collect: {currentQuestion.q} = ?</div>
              </div>
            )}

            <div className="relative bg-gradient-to-r from-green-600 to-green-800 rounded-lg overflow-hidden" style={{ height: '400px' }}>
              {/* Road lines */}
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-full h-0.5 bg-white opacity-30"
                  style={{ top: `${i * 25}%` }}
                />
              ))}

              {/* Player */}
              <div
                className="absolute left-8 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-2xl transition-all duration-200"
                style={{ top: `${playerY}%`, transform: 'translateY(-50%)' }}
              >
                🏃
              </div>

              {/* Obstacles */}
              {obstacles.map(obs => (
                <div
                  key={obs.id}
                  className="absolute w-10 h-10 bg-red-600 rounded flex items-center justify-center text-2xl"
                  style={{
                    left: `${obs.x}%`,
                    top: `${obs.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  ⚠️
                </div>
              ))}

              {/* Numbers */}
              {correctNumbers.map(num => (
                <div
                  key={num.id}
                  className={`absolute w-12 h-12 rounded-lg flex items-center justify-center text-xl font-bold ${
                    num.isWrong ? 'bg-red-500 text-white' : 'bg-yellow-400 text-gray-800'
                  }`}
                  style={{
                    left: `${num.x}%`,
                    top: `${num.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  {num.value}
                </div>
              ))}
            </div>

            <div className="text-center mt-4 text-white text-lg">
              Use ↑ ↓ Arrow Keys to Move
            </div>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🏁</div>
            <h2 className="text-4xl font-bold mb-4">Race Complete!</h2>
            <div className="text-3xl mb-4 text-blue-600 font-bold">Score: {score}</div>
            <div className="text-2xl mb-8 text-gray-600">Distance: {distance}m</div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              RUN AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberRunner;
